import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProductCard from '../../components/common/ProductCardComponent/Index';
import Header from '../../assests/Header.png';
import FilterSidebar from '../../components/FilterSidebar/index';
import ReactPaginate from 'react-paginate';
import { fetchProductData } from '../../Services/ProductServices';
import Spinner from '../../components/common/loadingSpinnercomponent/Spinner';


const SearchResult = () => {
  const [allProducts, setAllProducts] = useState([]); // All fetched products
  const [filteredProducts, setFilteredProducts] = useState([]); // Products after applying filters and sorting
  const [currentPage, setCurrentPage] = useState(0); // Current page for pagination
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const itemsPerPage = 12; // Number of items per page
  const location = useLocation();
  
  // Define the state for filters and search
  const [filters, setFilters] = useState({
    category: null,
    size: null,
    availability: null,
    minPrice: null,
    maxPrice: null,
    searchQuery: null,
  });

  // State for sorting
  const [sortOption, setSortOption] = useState('latest'); // Default sort option

  /**
   * Initialize filters, sort option, and search query from URL when component mounts
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const initialFilters = {
      category: urlParams.get('category') || null,
      size: urlParams.get('size') || null,
      availability: urlParams.get('availability') || null,
      minPrice: urlParams.get('min_price') ? parseInt(urlParams.get('min_price'), 10) : null,
      maxPrice: urlParams.get('max_price') ? parseInt(urlParams.get('max_price'), 10) : null,
      searchQuery: urlParams.get('query') || null,
    };
    const initialSort = urlParams.get('sort') || 'latest';
    setFilters(initialFilters);
    setSortOption(initialSort);
  }, [location.search]);

  /**
   * Handle filter changes from FilterSidebar
   */
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }));
    setCurrentPage(0);
    console.log("Updated Filters:", newFilters);
  };

  /**
   * Build query parameters string based on filters and search query
   */
  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();

    // Always include all filter params, using empty strings if not set
    if (filters.searchQuery) {
      params.set('query', filters.searchQuery);
    }
    params.set('category', filters.category || '');
    params.set('size', filters.size || '');
    params.set('availability', filters.availability || '');
    params.set('min_price', filters.minPrice !== null ? filters.minPrice : '');
    params.set('max_price', filters.maxPrice !== null ? filters.maxPrice : '');

    return params.toString();
  };

  /**
   * Update URL with current filters and sort option
   */
  const updateUrlWithQueryString = (queryString) => {
    const params = new URLSearchParams(queryString);
    params.set('sort', sortOption);
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ path: url }, '', url);
  };

  /**
   * Fetch and filter products based on current filters and search query
   */
  const fetchDataWithFilters = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const queryString = buildQueryParams(filters);
      updateUrlWithQueryString(queryString);
      const productsData = await fetchProductData(filters.searchQuery || '');
      
      let filtered = productsData;
      
      if (filters.category) {
        filtered = filtered.filter(product => 
          product.Category.toLowerCase() === filters.category.toLowerCase()
        );
      }
      if (filters.size) {
        filtered = filtered.filter(product => 
          product.Size === filters.size
        );
      }
      if (filters.availability) {
        filtered = filtered.filter(product => 
          product.StockStatus === filters.availability
        );
      }
      if (filters.minPrice !== null) {
        filtered = filtered.filter(product => 
          getDisplayPrice(product) >= filters.minPrice
        );
      }
      if (filters.maxPrice !== null) {
        filtered = filtered.filter(product => 
          getDisplayPrice(product) <= filters.maxPrice
        );
      }

      setAllProducts(productsData);
      setFilteredProducts(filtered);
      setCurrentPage(0);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to fetch products. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Helper function to get display price
   */
  const getDisplayPrice = (product) => {
    return product.OfferPrice > 0 ? product.OfferPrice : product.Price;
  };

  /**
   * Fetch products when filters change
   */
  useEffect(() => {
    fetchDataWithFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  /**
   * Handle sorting
   */
  useEffect(() => {
    if (!isLoading && !error) {
      let sortedProducts = [...filteredProducts];

      switch (sortOption) {
        case 'price_low':
          sortedProducts.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
          break;
        case 'price_high':
          sortedProducts.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
          break;
        case 'latest':
        default:
          sortedProducts.sort((a, b) => new Date(b.DateCreated) - new Date(a.DateCreated));
          break;
      }

      setFilteredProducts(sortedProducts);
      setCurrentPage(0); // Reset to first page when sort changes
    }
  }, [sortOption]);

  /**
   * Handle browser navigation
   */
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const updatedFilters = {
        category: urlParams.get('category') || null,
        size: urlParams.get('size') || null,
        availability: urlParams.get('availability') || null,
        minPrice: urlParams.get('min_price') ? parseInt(urlParams.get('min_price'), 10) : null,
        maxPrice: urlParams.get('max_price') ? parseInt(urlParams.get('max_price'), 10) : null,
        searchQuery: urlParams.get('query') || null,
      };
      const updatedSort = urlParams.get('sort') || 'latest';
      setFilters(updatedFilters);
      setSortOption(updatedSort);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /**
   * Handle page change in pagination
   */
  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Retrieve the products for the current page
   */
  const getPagedData = () => {
    if (!filteredProducts || filteredProducts.length === 0) {
      return [];
    }
    const offset = currentPage * itemsPerPage;
    return filteredProducts.slice(offset, offset + itemsPerPage);
  };

  const currentItems = getPagedData();
  const startItem = currentPage * itemsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * itemsPerPage, filteredProducts.length || 0);

  return (
    <div>
      {isLoading && (
        <div className="loading-container">
          <Spinner />
        </div>
      )}
      {error && <div className="error-container">Error: {error}</div>}
      {!isLoading && !error && (
        <>
          <header className="header-image-container">
            <img src={Header} alt="Header" className="img-fluid" />
          </header>
          <div className="row mt-4 mx-0">
            <h1 className="h4 heading">
              {filters.searchQuery 
                ? `Search Results for "${filters.searchQuery}"`
                : 'All Products'}
            </h1>
            <div className="col text-right">
              <span className="items-count">
                Showing {endItem} of {filteredProducts?.length || 0} results
              </span>
              <div className="product-dropdown-container">
                <select
                  className="custom-dropdown"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                >
                  <option value="latest">Sort By Latest</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                </select>
                <i className="fa-solid fa-chevron-down dropdown-icon"></i>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-3">
                <FilterSidebar
                  onFilterChange={handleFilterChange}
                  initialFilters={filters}
                />
              </div>
              <div className="col-lg-9">
                {currentItems.length === 0 ? (
                  <div className="no-products">
                    No products found. Please adjust your filters or search terms.
                  </div>
                ) : (
                  <div className="product-list-grid">
                    {currentItems.map((product) => (
                      <ProductCard key={product.ProductId} product={product} />
                    ))}
                  </div>
                )}
                {filteredProducts.length > 0 && (
                  <div className="pagination-container">
                    <ReactPaginate
                      previousLabel={<i className="fa-solid fa-chevron-left"></i>}
                      nextLabel={<i className="fa-solid fa-chevron-right"></i>}
                      breakLabel="..."
                      pageCount={Math.ceil(filteredProducts.length / itemsPerPage)}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={5}
                      onPageChange={handlePageClick}
                      containerClassName="pagination"
                      activeClassName="active"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      previousClassName="page-item"
                      previousLinkClassName="page-link"
                      nextClassName="page-item"
                      nextLinkClassName="page-link"
                      breakClassName="page-item"
                      breakLinkClassName="page-link"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SearchResult;